import { useState } from 'react';

import { TableColumnsType } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import IconDown from '@/assets/icons/icon-down.svg?react';
import IconRight from '@/assets/icons/icon-right.svg?react';
import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { converToMillion } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: string;
  name: string | JSX.Element;
  total: string;
  [key: string]: string | number | JSX.Element | undefined;
}

const PNLStorageTable = () => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const resultId = useParams<Record<string, string>>();
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId.resultId}/yearly_financial_analysis/`
    );

  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId.resultId}/`
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onExpand = (expanded: boolean, record: any) => {
    const keys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter((key) => key !== record.key);
    setExpandedRowKeys(keys);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customExpandIcon = (props: any) => {
    if (!props.record.children) {
      return <span style={{ marginRight: '22px' }} />;
    }
    if (props.expanded) {
      return (
        <IconDown
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    } else {
      return (
        <IconRight
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    }
  };

  let years: number[] = [];

  financialData?.graphData.forEach((item) => {
    years.push(item.year);
  });

  const scrollX = 775 + years.length * 100;

  years = [...new Set(years)].sort((a, b) => a - b);

  if (!financialData || !simulationData) {
    return null;
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 350,
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.total'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    ...years.map((year) => ({
      title: year.toString(),
      dataIndex: year.toString(),
      key: year.toString(),
      width: 100,
    })),
  ];

  const getValuesForYears = (
    graphData: SimulationResultYearlyFinancialAnalysisData[],
    key: string
  ) => {
    const values: Record<string, string | number> = {};
    if (graphData) {
      years.forEach((year) => {
        const data = graphData.find((item) => item.year === year);
        values[year.toString()] = data
          ? converToMillion(data[key]) + ` ${currencySymbol}`
          : '0';
      });
    }
    return values;
  };

  financialData.graphData.forEach((item) => {
    const totalDepreciation = item.storageDepreciation + item.plantDepreciation;
    item.totalDepreciation = totalDepreciation;
  });

  const dataSource = [
    {
      key: '1',
      name: t('result.finance.profitAndLoss.tables.rows.revenuesBattery'),
      total:
        converToMillion(simulationData?.result.revenueFromStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'revenueFromStorage'),
      children: [
        {
          key: '1-1',
          name: t(
            'result.finance.profitAndLoss.tables.rows.additionalRevenueFromArbitrage'
          ),
          total:
            converToMillion(simulationData?.result.revenueFromArbitrage) +
            ` ${currencySymbol}`,
          ...getValuesForYears(financialData.graphData, 'revenueFromArbitrage'),
        },
        {
          key: '1-2',
          name: t(
            'result.finance.profitAndLoss.tables.rows.revenueFromOfferedCapacity'
          ),
          total:
            converToMillion(simulationData?.result.revenueFromOfferedCapacity) +
            ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'revenueFromOfferedCapacity'
          ),
        },
      ],
    },
    // {
    //   key: '11',
    //   name: t('result.finance.profitAndLoss.tables.rows.residualValues'),
    //   total:
    //     converToMillion(simulationData?.result.residualValueOfStorage) + ` ${currencySymbol}`,
    //   ...getValuesForYears(financialData.graphData, 'residualValueOfStorage'),
    // },
    {
      key: '2',
      name: t(
        'result.finance.profitAndLoss.tables.rows.additionalCostOfStorage'
      ),
      total:
        converToMillion(simulationData?.result.variableCostsOfStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'variableCostsOfStorage'),
    },
    {
      key: '3',
      name: t('result.finance.profitAndLoss.tables.rows.grossProfitOfStorage'),
      total:
        converToMillion(simulationData?.result.grossProfitStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'grossProfitStorage'),
    },
    {
      key: '4',
      name: t('result.finance.profitAndLoss.tables.rows.totalStorageOpex'),
      total:
        converToMillion(simulationData?.result.storageOpex) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'storageOpex'),
    },
    {
      key: '5',
      name: t('result.finance.profitAndLoss.tables.rows.ebitdaStorage'),
      total:
        converToMillion(simulationData?.result.ebitdaStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebitdaStorage'),
    },
    {
      key: '6',
      name: t('result.finance.profitAndLoss.tables.rows.depreciationStorage'),
      ...getValuesForYears(financialData.graphData, 'storageDepreciation'),
    },
    {
      key: '7',
      name: t('result.finance.profitAndLoss.tables.rows.ebitStorage'),
      total:
        converToMillion(simulationData?.result.ebitStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebitStorage'),
    },
    {
      key: '8',
      name: t(
        'result.finance.profitAndLoss.tables.rows.interestExpenseStorage'
      ),
      total:
        converToMillion(simulationData?.result.loanInterestPaymentForStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(
        financialData.graphData,
        'loanInterestPaymentForStorage'
      ),
    },
    {
      key: '9',
      name: t('result.finance.profitAndLoss.tables.rows.taxesStorage'),
      total:
        converToMillion(simulationData?.result.incomeTaxStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'incomeTaxStorage'),
    },
    {
      key: '10',
      name: t('result.finance.profitAndLoss.tables.rows.netIncomeStorage'),
      total:
        converToMillion(simulationData?.result.netIncomeStorage) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'netIncomeStorage'),
    },
  ];

  return (
    <StyledProfit>
      <Table
        dataSource={dataSource}
        columns={columns}
        expandable={{
          expandedRowKeys,
          onExpand,
          expandIcon: customExpandIcon,
          rowExpandable: (record) => !!record.children,
        }}
        pagination={false}
        scroll={{ x: scrollX }}
      />
    </StyledProfit>
  );
};

export default PNLStorageTable;
